import { useState, useEffect, useCallback, useRef } from 'react';
import { QuizSettings, QuizState } from '../types/quiz';
import { fetchQuizQuestions } from '../services/quizService';

const useQuiz = (settings: QuizSettings) => {
  // Use a ref to track game over state to avoid infinite loops
  const isGameOverRef = useRef(false);
  
  const [state, setState] = useState<QuizState>({
    questions: [],
    currentQuestionIndex: 0,
    score: 0,
    lives: settings.lives,
    skipsRemaining: settings.skips, // Use the skips value from settings
    gameOver: false,
    loading: true,
    error: null
  });

  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [answerChecked, setAnswerChecked] = useState<boolean>(false);

  const loadQuestions = useCallback(async (isInitialLoad = false) => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const questions = await fetchQuizQuestions(settings);
      
      setState(prev => ({
        ...prev,
        questions: isInitialLoad ? questions : [...prev.questions, ...questions],
        loading: false,
        error: null
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load questions'
      }));
    }
  }, [settings]);

  // Initial load of questions
  useEffect(() => {
    loadQuestions(true);
  }, [loadQuestions]);

  // Update the ref when state.gameOver changes
  useEffect(() => {
    isGameOverRef.current = state.gameOver;
  }, [state.gameOver]);
  
  useEffect(() => {
    // Game over when lives are depleted
    if (state.lives <= 0 && !isGameOverRef.current) {
      setState(prev => ({ ...prev, gameOver: true }));
      return;
    }
    
    // For standard mode, game over when all questions are answered
    if (settings.mode === 'standard' && 
        state.currentQuestionIndex >= state.questions.length && 
        state.questions.length > 0 && 
        !isGameOverRef.current) {
      setState(prev => ({ ...prev, gameOver: true }));
      return;
    }
    
    // For endless mode, load more questions when we're getting close to the end
    if (settings.mode === 'endless' && 
        state.currentQuestionIndex >= state.questions.length - 3 && 
        state.questions.length > 0 && 
        !state.loading && 
        !isGameOverRef.current) {
      loadQuestions(false);
    }
  }, [state.lives, state.currentQuestionIndex, state.questions.length, state.loading, settings.mode, loadQuestions]);

  const handleAnswerClick = (answer: string) => {
    if (answerChecked || isGameOverRef.current) return;
    
    setSelectedAnswer(answer);
    setAnswerChecked(true);
    
    const currentQuestion = state.questions[state.currentQuestionIndex];
    const isCorrect = answer === currentQuestion.correct_answer;
    
    if (isCorrect) {
      setState(prev => ({
        ...prev,
        score: prev.score + 1
      }));
      
      // Move to next question after a delay for correct answers
      setTimeout(() => {
        if (!isGameOverRef.current) {
          setSelectedAnswer(null);
          setAnswerChecked(false);
          setState(prev => ({
            ...prev,
            currentQuestionIndex: prev.currentQuestionIndex + 1
          }));
        }
      }, 1500);
    } else {
      // Handle incorrect answer
      const newLives = state.lives - 1;
      
      // Set game over if this was the last life
      if (newLives <= 0) {
        isGameOverRef.current = true; // Set ref immediately to prevent race conditions
        setState(prev => ({
          ...prev,
          lives: 0
        }));
        
        // Add a delay before setting gameOver to true so the user can see the correct answer
        setTimeout(() => {
          setState(prev => ({
            ...prev,
            gameOver: true
          }));
        }, 2500); // 2 second delay
      } else {
        setState(prev => ({
          ...prev,
          lives: newLives
        }));
        
        // Move to next question after a delay for incorrect answers with remaining lives
        setTimeout(() => {
          if (!isGameOverRef.current) {
            setSelectedAnswer(null);
            setAnswerChecked(false);
            setState(prev => ({
              ...prev,
              currentQuestionIndex: prev.currentQuestionIndex + 1
            }));
          }
        }, 1500);
      }
    }
  };

  const skipQuestion = () => {
    if (!answerChecked && state.skipsRemaining > 0) {
      setState(prev => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex + 1,
        skipsRemaining: prev.skipsRemaining - 1
      }));
    }
  };

  const resetQuiz = () => {
    isGameOverRef.current = false; // Reset the game over ref
    setState({
      questions: [],
      currentQuestionIndex: 0,
      score: 0,
      lives: settings.lives,
      skipsRemaining: settings.skips, // Reset skips to the value from settings
      gameOver: false,
      loading: true,
      error: null
    });
    setSelectedAnswer(null);
    setAnswerChecked(false);
  };

  return {
    state,
    selectedAnswer,
    answerChecked,
    handleAnswerClick,
    skipQuestion,
    resetQuiz,
    loadQuestions
  };
};

export default useQuiz;
